import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import type {
  ProfileExposureSetting,
  ProfileFooter,
  ProfileHeader,
  ProfileHeaderUpdate,
  ProfileMediaLayout,
  ProfileStatement,
  ProfileTextBlock,
  ProfileWork,
  WorkFilterTag,
  WorkInput,
} from "../../types/profile";

// ===== 型定義 =====

export interface ProfileSettings {
  vanity_slug: string;
  exposure_setting: ProfileExposureSetting;
  inquiry_handling: string;
}

export interface ProfileContent {
  statement: ProfileStatement | null;
  header: ProfileHeader | null;
  profile_texts: ProfileTextBlock[];
  works: ProfileWork[];
  /** 作品タグ並び順マスタ（ユーザースコープ）。実在タグでフィルタ済み + 未登録分は末尾。 */
  work_tag_order: string[];
  /** 作品フィルタータグマスタ（position 順）。ハッシュタグとは別系統。 */
  work_filter_tags: WorkFilterTag[];
  /** 作品展示方法（thumbnail / circle / list）。 */
  work_display_layout: ProfileMediaLayout;
  footer: ProfileFooter | null;
  is_published: boolean;
}

/** 編集 UI 向けのレビュー候補（受け取ったレビュー）。 */
export interface ProfileReviewCandidate {
  id: number;
  note: string;
  reviewer_name: string | null;
  is_anonymous: boolean;
  rating: "LIKE" | "DISLIKE" | null;
  /** ReviewOffer.status: "1"=実名OK / "2"=匿名OK / "3"=許可しない。 */
  status: string;
  show_on_profile: boolean;
  profile_order: number;
  created: string | null;
}

interface SettingsResponse {
  status: string;
  data: ProfileSettings;
}

interface ContentResponse {
  status: string;
  data: ProfileContent;
}

interface PublishResponse {
  status: string;
  data: { is_published: boolean };
}

interface ReviewsResponse {
  status: string;
  data: { reviews: ProfileReviewCandidate[] };
}

interface WorksResponse {
  status: string;
  data: { works: ProfileWork[]; id?: string };
}

interface WorkTagOrderResponse {
  status: string;
  data: { tags: string[] };
}

interface WorkFilterTagsResponse {
  status: string;
  data: { tags: WorkFilterTag[] };
}

/** work-filter-tags PUT の 1 エントリ。id 無しは新規作成。 */
export interface WorkFilterTagInput {
  id?: string;
  name: string;
}

/** content PUT に渡す部分更新ペイロード。header はメディア差し替え用 file_key を含められる。 */
export type ProfileContentUpdate = Partial<
  Pick<ProfileContent, "statement" | "profile_texts" | "footer" | "work_display_layout">
> & { header?: ProfileHeaderUpdate };

// ===== API スライス =====

export const creatorProfileApi = createApi({
  reducerPath: "creatorProfileApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["ProfileSettings", "ProfileContent", "ProfileReviews"],
  endpoints: (builder) => ({
    getProfileSettings: builder.query<SettingsResponse, void>({
      query: () => "/api/me/pages/profile/settings",
      providesTags: ["ProfileSettings"],
    }),
    // リアルタイム編集のため再フェッチによる入力中断を避ける（ローカル state 優先）。
    updateProfileSettings: builder.mutation<SettingsResponse, Partial<ProfileSettings>>({
      query: (body) => ({
        url: "/api/me/pages/profile/settings",
        method: "PATCH",
        body,
      }),
    }),

    getProfileContent: builder.query<ContentResponse, void>({
      query: () => "/api/me/pages/profile/content",
      providesTags: ["ProfileContent"],
    }),
    // リアルタイム編集中の再フェッチによる入力中断を避けるため、content 更新では
    // タグ無効化しない（ローカル state を編集中の真実とする）。公開状態など他の
    // 読み取りは publish トグル側で明示的に無効化する。
    updateProfileContent: builder.mutation<ContentResponse, ProfileContentUpdate>({
      query: (body) => ({
        url: "/api/me/pages/profile/content",
        method: "PUT",
        body,
      }),
    }),

    publishProfile: builder.mutation<PublishResponse, boolean>({
      query: (published) => ({
        url: "/api/me/pages/profile/publish",
        method: "POST",
        body: { published },
      }),
      invalidatesTags: ["ProfileContent"],
    }),

    getProfileReviews: builder.query<ReviewsResponse, void>({
      query: () => "/api/me/pages/profile/reviews",
      providesTags: ["ProfileReviews"],
    }),
    // 掲載 ON/OFF の切替専用。並び順は `reorderProfileReviews` を使う。
    updateProfileReview: builder.mutation<
      unknown,
      { id: number; show_on_profile: boolean }
    >({
      query: ({ id, show_on_profile }) => ({
        url: `/api/me/pages/profile/reviews/${id}`,
        method: "PATCH",
        body: { show_on_profile },
      }),
      invalidatesTags: ["ProfileReviews"],
    }),
    reorderProfileReviews: builder.mutation<ReviewsResponse, number[]>({
      query: (ids) => ({
        url: "/api/me/pages/profile/reviews/reorder",
        method: "POST",
        body: { ids },
      }),
      invalidatesTags: ["ProfileReviews"],
    }),

    // ===== 作品 CRUD =====
    // レスポンスは常に全 works を返すため、ProfileContent キャッシュを invalidate して
    // 一覧側を再取得させる（ProfileContent は MySiteWorks も購読している）。
    createProfileWork: builder.mutation<WorksResponse, WorkInput>({
      query: (body) => ({
        url: "/api/me/pages/profile/works",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ProfileContent"],
    }),
    updateProfileWork: builder.mutation<
      WorksResponse,
      { id: number | string; body: WorkInput }
    >({
      query: ({ id, body }) => ({
        url: `/api/me/pages/profile/works/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ProfileContent"],
    }),
    deleteProfileWork: builder.mutation<WorksResponse, number | string>({
      query: (id) => ({
        url: `/api/me/pages/profile/works/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProfileContent"],
    }),
    reorderProfileWorks: builder.mutation<WorksResponse, (number | string)[]>({
      query: (ids) => ({
        url: "/api/me/pages/profile/works/reorder",
        method: "POST",
        body: { ids },
      }),
      invalidatesTags: ["ProfileContent"],
    }),

    // ===== 作品タグ並び順マスタ =====
    // ProfileContent.work_tag_order と同期するため、変更時は ProfileContent を invalidate。
    updateProfileWorkTagOrder: builder.mutation<WorkTagOrderResponse, string[]>({
      query: (tags) => ({
        url: "/api/me/pages/profile/work-tag-order",
        method: "PUT",
        body: { tags },
      }),
      invalidatesTags: ["ProfileContent"],
    }),

    // ===== 作品フィルタータグマスタ（ハッシュタグとは別系統） =====
    // 全置換 PUT。含まれない既存タグは削除され、削除タグは全作品の
    // filter_tag_ids からも除去される（BE 側で一括処理）。
    updateProfileWorkFilterTags: builder.mutation<
      WorkFilterTagsResponse,
      WorkFilterTagInput[]
    >({
      query: (tags) => ({
        url: "/api/me/pages/profile/work-filter-tags",
        method: "PUT",
        body: { tags },
      }),
      invalidatesTags: ["ProfileContent"],
    }),
  }),
});

export const {
  useGetProfileSettingsQuery,
  useUpdateProfileSettingsMutation,
  useGetProfileContentQuery,
  useUpdateProfileContentMutation,
  usePublishProfileMutation,
  useGetProfileReviewsQuery,
  useUpdateProfileReviewMutation,
  useReorderProfileReviewsMutation,
  useCreateProfileWorkMutation,
  useUpdateProfileWorkMutation,
  useDeleteProfileWorkMutation,
  useReorderProfileWorksMutation,
  useUpdateProfileWorkTagOrderMutation,
  useUpdateProfileWorkFilterTagsMutation,
} = creatorProfileApi;
